// frontend/src/components/SiteProcessingView.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function SiteProcessingView({ siteId, farmId, onComplete }) {
  const [activeTab, setActiveTab] = useState('mounting');
  const [dots, setDots] = useState('.');
  const [isReady, setIsReady] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // 1. Polling for completed analytics from site_states
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`/api/farms/${farmId}/summary`, { withCredentials: true });
        const match = res.data?.plots?.find((p) => p.site_id === siteId);
        
        if (match) {
          clearInterval(interval);
          setSummaryData(res.data);
          setIsReady(true); // Enable the View Dashboard button
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [siteId, farmId]);

  // Animated dots for header indicator
  useEffect(() => {
    if (isReady) return;
    const dotInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '.' : prev + '.'));
    }, 500);
    return () => clearInterval(dotInterval);
  }, [isReady]);

  return (
    <div className="processing-container">
      {/* Dynamic Header Status */}
      <div className={`processing-header ${isReady ? 'ready' : ''}`}>
        <div className={isReady ? 'check-icon' : 'spinner'}>
          {isReady && '✓'}
        </div>
        <div>
          <h3>
            {isReady 
              ? 'Site Initialized & Ready!' 
              : `Calculating Micro-Climate & Risk Models${dots}`}
          </h3>
          <p className="wizard-subtext">
            {isReady
              ? 'Historical observation baselines and agronomic risk models have been computed.'
              : 'We are pulling historical satellite observations and computing agronomic risk baselines.'}
          </p>
        </div>
      </div>

      {/* Educational Guide Navigation */}
      <div className="guide-tabs">
        <button
          className={`guide-tab ${activeTab === 'mounting' ? 'active' : ''}`}
          onClick={() => setActiveTab('mounting')}
        >
          📍 Hardware Mounting
        </button>
        <button
          className={`guide-tab ${activeTab === 'network' ? 'active' : ''}`}
          onClick={() => setActiveTab('network')}
        >
          📡 Network Layout
        </button>
        <button
          className={`guide-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard Guide
        </button>
      </div>

      {/* Guide Content Cards */}
      <div className="guide-card">
        {activeTab === 'mounting' && (
          <div className="guide-body">
            <h4>Device Mounting Guidelines</h4>
            <p>Ensure maximum sensor accuracy by following recommended field installation heights:</p>
            <ul>
              <li>
                <strong>Trellis & Canopy Mounts:</strong> Mount temperature/humidity sensors at 2/3 canopy height, facing away from direct south exposure.
              </li>
              <li>
                <strong>Weather Station Pole Mount:</strong> Secure main stations on a 2-meter vertical pole with a 30-degree clear overhead cone.
              </li>
              <li>
                <strong>Soil Probe Installation:</strong> Insert probes vertically at 15cm and 45cm depths with firm soil contact.
              </li>
            </ul>
          </div>
        )}

        {activeTab === 'network' && (
          <div className="guide-body">
            <h4>LoRaWAN & Cellular Network Layout</h4>
            <p>Optimize your gateway placement for reliable sensor transmission across field plots:</p>
            <ul>
              <li>
                <strong>Gateway Elevation:</strong> Mount gateways at least 3–5 meters high to achieve optimal range.
              </li>
              <li>
                <strong>Node Density:</strong> Place nodes within 2km of central gateways in dense canopy conditions.
              </li>
              <li>
                <strong>Solar Angles:</strong> Ensure solar panels face South at 45° angle.
              </li>
            </ul>
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="guide-body">
            <h4>What to Explore on Your Dashboard</h4>
            <p>Once initialization finishes, your site dashboard will unlock key analytics:</p>
            <ul>
              <li>
                <strong>Vapor Pressure Deficit (VPD):</strong> Monitor transpiration stress levels.
              </li>
              <li>
                <strong>Growing Degree Days (GDD):</strong> Track cumulative thermal time for phenology.
              </li>
              <li>
                <strong>Infection Risk Alerts:</strong> Disease models dynamically evaluate risk trends.
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Bottom Action Button (Grayed out while processing, enabled when ready) */}
      <div className="processing-actions">
        <button
          className="btn-primary-lg btn-dashboard"
          disabled={!isReady}
          onClick={() => isReady && onComplete(summaryData)}
        >
          {isReady ? 'View Dashboard' : 'Processing Regional Data...'}
        </button>
      </div>
    </div>
  );
}
